import { useState } from 'react'
import { useSettingsStore } from '@/stores/settings'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { 
  Palette, 
  Sun, 
  Moon, 
  Monitor, 
  Globe,
  Calendar,
  Clock,
  Layout,
  Sidebar,
  Save,
  RefreshCw
} from 'lucide-react'
import { toast } from 'sonner'

export function AppearanceSettings() {
  const { appearance, updateAppearance, loading } = useSettingsStore()
  const [hasChanges, setHasChanges] = useState(false)
  const [formData, setFormData] = useState(appearance)

  const handleFieldChange = (field: keyof typeof appearance, value: any) => {
    const newData = { ...formData, [field]: value }
    setFormData(newData)
    setHasChanges(true)
  }

  const handleSave = () => {
    updateAppearance(formData)
    setHasChanges(false)
    toast.success('Appearance settings saved!')
  }

  const handleReset = () => {
    setFormData(appearance)
    setHasChanges(false)
  }

  const themeOptions = [
    { value: 'light', label: 'Light', icon: Sun, description: 'Light mode' },
    { value: 'dark', label: 'Dark', icon: Moon, description: 'Dark mode' },
    { value: 'system', label: 'System', icon: Monitor, description: 'Follow system preference' }
  ]

  const languages = [
    { value: 'en', label: 'English' },
    { value: 'es', label: 'Español' },
    { value: 'fr', label: 'Français' },
    { value: 'de', label: 'Deutsch' },
    { value: 'it', label: 'Italiano' },
    { value: 'pt', label: 'Português' },
    { value: 'ja', label: '日本語' },
    { value: 'ko', label: '한국어' },
    { value: 'zh', label: '中文' }
  ]

  const dateFormats = [
    { value: 'MM/DD/YYYY', label: 'MM/DD/YYYY', example: '12/31/2023' },
    { value: 'DD/MM/YYYY', label: 'DD/MM/YYYY', example: '31/12/2023' },
    { value: 'YYYY-MM-DD', label: 'YYYY-MM-DD', example: '2023-12-31' }
  ]

  const timeFormats = [
    { value: '12h', label: '12 hour', example: '2:30 PM' },
    { value: '24h', label: '24 hour', example: '14:30' }
  ]

  const densityOptions = [
    { value: 'comfortable', label: 'Comfortable', description: 'More spacing and larger elements' },
    { value: 'compact', label: 'Compact', description: 'Tighter spacing and smaller elements' }
  ]

  return (
    <div className="space-y-6">
      {/* Theme Selection */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Palette className="h-5 w-5" />
            <span>Theme</span>
          </CardTitle>
          <CardDescription>
            Choose your preferred theme
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-4">
            {themeOptions.map((theme) => {
              const Icon = theme.icon
              const isSelected = formData.theme === theme.value
              
              return (
                <button
                  key={theme.value}
                  onClick={() => handleFieldChange('theme', theme.value)}
                  className={`p-4 border rounded-lg transition-all ${
                    isSelected 
                      ? 'border-primary bg-primary/10' 
                      : 'border-border hover:border-primary/50'
                  }`}
                >
                  <div className="flex flex-col items-center space-y-2">
                    <Icon className={`h-8 w-8 ${isSelected ? 'text-primary' : 'text-muted-foreground'}`} />
                    <div className="font-medium">{theme.label}</div>
                    <div className="text-xs text-muted-foreground text-center">
                      {theme.description}
                    </div>
                  </div>
                </button>
              )
            })}
          </div>
        </CardContent>
      </Card>

      {/* Language & Region */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Globe className="h-5 w-5" />
            <span>Language & Region</span>
          </CardTitle>
          <CardDescription>
            Set your language and regional preferences
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Language</Label>
              <Select value={formData.language} onValueChange={(value) => handleFieldChange('language', value)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select language" />
                </SelectTrigger>
                <SelectContent>
                  {languages.map((lang) => (
                    <SelectItem key={lang.value} value={lang.value}>
                      {lang.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Date & Time Format */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Calendar className="h-5 w-5" />
            <span>Date & Time Format</span>
          </CardTitle>
          <CardDescription>
            Choose how dates and times are displayed
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Date Format</Label>
                <Select value={formData.date_format} onValueChange={(value) => handleFieldChange('date_format', value)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select date format" />
                  </SelectTrigger>
                  <SelectContent>
                    {dateFormats.map((format) => (
                      <SelectItem key={format.value} value={format.value}>
                        <div className="flex items-center justify-between w-full">
                          <span>{format.label}</span>
                          <span className="text-muted-foreground text-sm ml-2">
                            {format.example}
                          </span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Time Format</Label>
                <Select value={formData.time_format} onValueChange={(value) => handleFieldChange('time_format', value)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select time format" />
                  </SelectTrigger>
                  <SelectContent>
                    {timeFormats.map((format) => (
                      <SelectItem key={format.value} value={format.value}>
                        <div className="flex items-center justify-between w-full">
                          <span>{format.label}</span>
                          <span className="text-muted-foreground text-sm ml-2">
                            {format.example}
                          </span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Display Density */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Layout className="h-5 w-5" />
            <span>Display Density</span>
          </CardTitle>
          <CardDescription>
            Control the spacing and size of interface elements
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {densityOptions.map((density) => (
              <div key={density.value} className="flex items-center justify-between p-4 border rounded-lg">
                <div className="flex-1">
                  <div className="font-medium">{density.label}</div>
                  <div className="text-sm text-muted-foreground">
                    {density.description}
                  </div>
                </div>
                <Switch
                  checked={formData.density === density.value}
                  onCheckedChange={(checked) => checked && handleFieldChange('density', density.value)}
                />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Sidebar Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Sidebar className="h-5 w-5" />
            <span>Sidebar</span>
          </CardTitle>
          <CardDescription>
            Configure sidebar behavior
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 border rounded-lg">
              <div className="flex-1">
                <div className="font-medium">Collapsed by Default</div>
                <div className="text-sm text-muted-foreground">
                  Start with sidebar collapsed on page load
                </div>
              </div>
              <Switch
                checked={formData.sidebar_collapsed}
                onCheckedChange={(checked) => handleFieldChange('sidebar_collapsed', checked)}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Preview */}
      <Card>
        <CardHeader>
          <CardTitle>Preview</CardTitle>
          <CardDescription>
            See how your preferences will look
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 border rounded-lg">
                <div className="font-medium">Current Theme</div>
                <div className="text-sm text-muted-foreground">
                  {themeOptions.find(t => t.value === formData.theme)?.label}
                </div>
              </div>
              <div className="p-4 border rounded-lg">
                <div className="font-medium">Language</div>
                <div className="text-sm text-muted-foreground">
                  {languages.find(l => l.value === formData.language)?.label}
                </div>
              </div>
            </div>

            <div className="p-4 border rounded-lg">
              <div className="font-medium">Date & Time Example</div>
              <div className="text-sm text-muted-foreground">
                {new Date().toLocaleDateString('en-US', { 
                  year: 'numeric', 
                  month: '2-digit', 
                  day: '2-digit' 
                }).replace(/(\d+)\/(\d+)\/(\d+)/, (match, m, d, y) => {
                  if (formData.date_format === 'DD/MM/YYYY') return `${d}/${m}/${y}`
                  if (formData.date_format === 'YYYY-MM-DD') return `${y}-${m}-${d}`
                  return match
                })} • {new Date().toLocaleTimeString('en-US', { 
                  hour12: formData.time_format === '12h',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Save Changes */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          {hasChanges && (
            <Badge variant="outline" className="text-orange-600">
              Unsaved changes
            </Badge>
          )}
        </div>
        
        <div className="flex items-center space-x-2">
          {hasChanges && (
            <Button variant="outline" onClick={handleReset}>
              Reset
            </Button>
          )}
          <Button 
            onClick={handleSave} 
            disabled={!hasChanges || loading}
            className="flex items-center space-x-2"
          >
            {loading ? (
              <RefreshCw className="h-4 w-4 animate-spin" />
            ) : (
              <Save className="h-4 w-4" />
            )}
            <span>{loading ? 'Saving...' : 'Save Changes'}</span>
          </Button>
        </div>
      </div>
    </div>
  )
} 